import ReferenceFieldWidgetUtil from "@/utils/editView/widgets/ReferenceFieldWidgetUtil.ts";
import {ref, Ref, shallowRef, ShallowRef, toValue, watch} from "vue";
import {ColDef, ColGroupDef, GridApi} from "ag-grid-community";
import {QSelectOption, QTreeNode} from "quasar";
import {CustomDTOExpression} from "@/utils/presenters/interfaces.ts";
import SearcherViewUtils from "@/utils/common/SearcherViewUtils.ts";
import ListViewUtils from "@/utils/common/ListViewUtils.ts";
import CriteriaTreeUtils from "@/utils/common/CriteriaTreeUtils.ts";
import NamaCommon, {NamaAxiosRequestConfig} from "@/utils/common/NamaCommon.ts";
import NamaURLS from "@/utils/constants/NamaURLS.ts";
import {useErrorsStore} from "@/stores/ToastDataStore.ts";
import AbsPresenter from "@/utils/common/AbsPresenter.ts";
import ListPresenter from "@/utils/common/ListPresenter.ts";
import Translator from "@/utils/common/Translator.ts";
import {StringUtils} from "@/utils/common/StringUtils.ts";
import SortType from "@/utils/constants/SortType.ts";
import CurrentUserUtils from "@/utils/login/CurrentUserUtils.ts";
import ReferenceGridFieldWidgetUtil from "@/utils/editView/widgets/ReferenceGridFieldWidgetUtil.ts";
import {FilterModel} from "ag-grid-enterprise";
import EditScreenFieldUtils from "@/utils/editView/editScreens/EditScreenFieldUtils.ts";
import ObjectChecker from "@/utils/common/ObjectChecker.ts";
import AGGridUtils from "@/utils/UIConstants/AGGridUtils.ts";
import StringConstants from "@/utils/constants/StringConstants.ts";
import SearcherScreenLayout = namaTypes.SearcherScreenLayout;
import CriteriaField = namaTypes.CriteriaField;
import DTOCriteria = namaTypes.DTOCriteria;
import AGGridListRequest = namaTypes.AGGridListRequest;
import FieldActionContextInfo = namaTypes.FieldActionContextInfo;
import ListViewQuickFilterGroupVal = namaTypes.ListViewQuickFilterGroupVal;
import ListViewQuickFilterSelectedValues = namaTypes.ListViewQuickFilterSelectedValues;
import ViewName = namaTypes.ViewName;

export default class SearcherViewPresenter extends AbsPresenter
{
  static readonly VALUE_TO_SEARCH = "searchValue";
  readonly sortDirections = [{
    label: Translator.translateId(ListViewUtils.ASC),
    value: ListViewUtils.ASC
  }, {
    label: Translator.translateId(ListViewUtils.DESC),
    value: ListViewUtils.DESC
  }
  ];
  columnDefs: Ref<(ColDef | ColGroupDef)[]> = ref([]);
  criteriaTree: Ref<QTreeNode<CustomDTOExpression>[]> = ref([]);
  quickFilterGroup: Ref<ListViewQuickFilterGroupVal[]> = ref([]);
  selectedQuickFilterGroups: Ref<ListViewQuickFilterSelectedValues[]> = ref([]);
  preferences: namaTypes.UIPreferences = {};
  override forSearch = true;
  listViewCriteria: DTOCriteria | undefined;
  gridApi: ShallowRef<GridApi | undefined> = shallowRef();
  sortOptions: Record<string, string>[] = [];
  searchColumnOptions: Record<string, string>[] = [];
  listViewNameValue: Ref<QSelectOption> = ref({} as QSelectOption);
  sortFieldsValue: Ref<Record<string, string>[]> = ref([]);
  searchInColumnsValue: Ref<Record<string, string>> = ref(this.createTheAllSearchOption());
  sortDirectionValue: Ref<Record<string, string>> = ref({});
  paginationPageSize: Ref<number, number> = ref(25);
  preventAutoLoad: Ref<boolean> = ref(false);
  contextInfo: FieldActionContextInfo;
  extraCriteria?:  DTOCriteria;
  isGridReferenceField: boolean;

  constructor(fieldUtil: ReferenceFieldWidgetUtil | ReferenceGridFieldWidgetUtil) {
    super()
    this._entityType = fieldUtil.referencedEntityType.value;
    this.isGridReferenceField = (fieldUtil instanceof ReferenceGridFieldWidgetUtil);
    let lineNumber = fieldUtil instanceof ReferenceGridFieldWidgetUtil ? (fieldUtil as ReferenceGridFieldWidgetUtil).row : -1;
    this.contextInfo = fieldUtil.presenter.createSearchContextInfo(fieldUtil.fieldId(), lineNumber);
    this.extraCriteria = fieldUtil.presenter.getDefaultCriteria(fieldUtil.fieldId());
    watch(toValue(this.selectedQuickFilterGroups), (__) => {
      if (ObjectChecker.isEmptyOrNull(this.quickFilterGroup.value))
        return;
      this.gridApi.value?.refreshServerSide({purge: true});
    })
  }

  async start() {
    this.preferences = await SearcherViewUtils.fetchLayoutPreferences(this.entityType);
    this.columnDefs.value = ListViewUtils.createDisplayColumns((this.layout?.displayColumns) ?? [], this.entityType, {
      editableLineNumber: this.isGridReferenceField,
      forSearcher: true,
      layout: this.layout
    });
    await this.initQuickFilterGroups();
    this.listViewCriteria = this.getDefaultCriteria();
    this.criteriaTree.value = CriteriaTreeUtils.criteriaToTree(this.getDefaultCriteria(), this.layout?.criteriaFields);
    this.record.value[ListPresenter.EXTRA_CRITERIA] = undefined;
    this.record.value[SearcherViewPresenter.VALUE_TO_SEARCH] = undefined;
    this.sortOptions = this.getSortByList();
    this.searchColumnOptions = this.getSearchInList();
    this.preventAutoLoad.value = this.layout?.searcherPreventAutoLoad ?? false;
    this.listViewNameValue.value = ListViewUtils.getListViewName(this.viewNames).find(l =>
      l.value === StringConstants.DEFAULT) ?? {} as QSelectOption;
    if (this.layout?.defaultOrderBy)
      this.sortFieldsValue.value = ListViewUtils.fetchCurrentSortValueList(this.sortOptions, this.layout?.defaultOrderBy);
    this.sortDirectionValue.value = this.sortDirections.find(l =>
      l.value === (this.layout?.defaultOrderBy === SortType.Descending ? ListViewUtils.DESC : ListViewUtils.ASC))!;
    this.paginationPageSize.value = AGGridUtils.fetchDefaultPageSize(this);
  }


  override getDefaultCriteria(fieldId?: string): DTOCriteria | undefined {
    return this.layout?.defaultCriteria;
  }

  get searchValue() {
    return this.record.value[SearcherViewPresenter.VALUE_TO_SEARCH];
  }

  getSortByList() {
    if (!this.layout?.sortableFields || !this.layout?.sortableFields.length)
      return [];
    let sortByList: Record<string, string>[] = [];
    for (let fieldId of this.layout.sortableFields)
      sortByList.push({
        value: fieldId,
        label: Translator.translateId(fieldId, this.entityType)!,
        searchData: EditScreenFieldUtils.fetchSearchTextForAllowedValue(fieldId, this.entityType)
      });
    return sortByList;
  }

  getSearchInList() {
    if (!this.layout?.searchableFields || !this.layout?.searchableFields.length)
      return [];
    let sortByList: Record<string, string>[] = [];
    sortByList.push(this.createTheAllSearchOption());
    for (let fieldId of this.layout.searchableFields)
      sortByList.push({
        value: fieldId,
        label: Translator.translateId(fieldId, this.entityType)!,
        searchData: EditScreenFieldUtils.fetchSearchTextForAllowedValue(fieldId, this.entityType)
      });
    return sortByList;
  }

  private createTheAllSearchOption() {
    return {
      value: "",
      label: Translator.translateId("theAll", this.entityType)!,
      searchData: EditScreenFieldUtils.fetchSearchTextForAllowedValue("thAll", this.entityType)
    };
  }

  get layout(): SearcherScreenLayout | undefined {
    return this.preferences.searcherLayout;
  }

  get viewNames(): ViewName[] | undefined {
    return this.preferences?.viewNames;
  }

  criteriaFields(): CriteriaField[] | undefined {
    return this.layout?.criteriaFields
  }

  override fetchDefaultPageSize(): string | undefined {
    if (this.layout && this.layout.searchDefaultPageSize)
      return this.layout.searchDefaultPageSize;
    return CurrentUserUtils.defaultSearchPageSize();
  }

  private createEmptyList(params) {
    let emptyRows = {
      rowData: [],
      rowCount: 0
    };
    if (params.success)
      params.success(emptyRows);
  }

  createServerSideDatasource() {
    return {
      getRows: async (params) => {
        let minSearchQueryLength = CurrentUserUtils.fetchMinSearchQueryLength(this.entityType);
        let valueToSearch = this.record.value[SearcherViewPresenter.VALUE_TO_SEARCH];
        if (this.preventAutoLoad.value) {
          this.createEmptyList(params);
          return;
        }
        if (valueToSearch && valueToSearch.length && valueToSearch.length < minSearchQueryLength)
          return;
        this.listViewCriteria = CriteriaTreeUtils.updateCriteriaFromTree(this.listViewCriteria, this.criteriaTree.value, this.layout?.criteriaFields);
        const filterModelFieldsType: Record<string, string | undefined> = {};
        if (params.request && params.request.filterModel) {
          const agGridFilterModel = params.request.filterModel as FilterModel;
          for (const field in agGridFilterModel)
            filterModelFieldsType[field] = this.layout?.displayColumns?.find(l => l.id === field)!.metaData?.fieldType;
        }
        let request: AGGridListRequest = this.getListRequest(params, valueToSearch, filterModelFieldsType);
        let toSend = {
          request,
          info: this.contextInfo
        };
        let settings: NamaAxiosRequestConfig = {
          data: toSend,
          noLoadingIndicator: true,
        }
        let data = await NamaCommon.namaFetch(NamaURLS.ListForSearchView, settings);
        if (data) {
          let listResult = data as namaTypes.ListEntityResult;
          if (listResult && listResult.result?.succeded) {
            let rows: any[] = [];
            if (listResult.page && listResult.page.length) {
              let dtos = listResult.page as namaTypes.DTOTabularResult[];
              rows = dtos.map(l => l.values)
            }
            let startIndex = params.startIndex ?? 0;
            for (let i = 0; i < rows.length; i++) {
              rows[i].namaRowIndex = (i + startIndex + 100).toString();
              if (this.isGridReferenceField)
                rows[i][ListViewUtils.REPEAT_COUNT_COLUMN_ID] = 0;
            }
            if (params.success) {
              params.success({
                rowData: rows,
                rowCount: listResult.totalRecordsCount
              });
            }
          } else if (listResult && listResult.result?.failed) {
            //todo
          }
        } else {
          useErrorsStore().pushErrorStr("Server Error");
        }
      },
    };
  }

  private getListRequest(params: any, valueToSearch?: any, filterModelFieldsType?: Record<string, string | undefined>) {
    return {
      entityType: this.entityType,
      owner: {
        ownerType: "Entity",
        ownerId: this.entityType
      },
      forSearch: this.forSearch,
      agGridRequest: params.request,
      displayedColumns: AGGridUtils.fetchDisplayedColumns(this.gridApi.value, this.columnDefs.value),
      criteria: this.listViewCriteria,
      extraCriteriaId: this.fetchExtraCriteriaIdFromRecord(),
      extraCriteria: this.extraCriteria,
      ascending: this.sortDirectionValue.value["value"] === ListViewUtils.ASC,
      orderBy: StringUtils.toCSVLineWithSep(',', this.sortFieldsValue.value.map(l => l.value)),
      valueToSearch: valueToSearch,
      columnsToSearchIn: this.searchInColumnsValue.value.value ? [this.searchInColumnsValue.value.value] : this.layout?.searchableFields,
      filterModelFieldsType: filterModelFieldsType,
      selectedQuickFilterGroups: this.selectedQuickFilterGroups.value
    };
  }

  fetchExtraCriteriaIdFromRecord() {
    return this.record.value[ListPresenter.EXTRA_CRITERIA] ? this.record.value[ListPresenter.EXTRA_CRITERIA].id : undefined;
  }

  onRefreshClicked() {
    if (this.gridApi.value)
      this.gridApi.value.refreshServerSide({purge: true});
  }

  async initQuickFilterGroups() {
    const quickFilterSelectedValues = this.selectedQuickFilterGroups.value;
    quickFilterSelectedValues.splice(0, quickFilterSelectedValues.length);
    const request: AGGridListRequest = this.getListRequest({});
    const settings: NamaAxiosRequestConfig = {
      data: {
        request,
        contextInfo: this.contextInfo
      },
      noLoadingIndicator: true,
      params: {
        entityType: this.entityType,
      }
    }
    const data = await NamaCommon.namaFetch(NamaURLS.FETCH_QUICK_FILTER_VALUES, settings)
    if (NamaCommon.isSuccess(data))
      this.quickFilterGroup.value = data.list as ListViewQuickFilterGroupVal[];
  }
}
